import {
  CalendarIcon,
  FoldersIcon,
  GraduationCapIcon,
  LineChartIcon,
  LucideIcon,
  MessageCircleIcon,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { Suspense } from 'react';
import routes from 'routes';

import { useWebHaptics } from 'web-haptics/react';
import Chat from './Chat';
import Training from './Training';
import { canUseAssistant, useGetTeam } from '@agently/api';
import { assistantCopy, CopilotAvatar } from '@agently/components';
import { isAssistantPage } from 'isAssistantPage';
import { useKeyboardOpen } from 'providers/KeyboardProvider';
import { cn, SuspenseBoundary } from '@agently/ui';
import DeferredMount from 'components/DeferredMount';

const TOUR_TARGET_BY_NAME: Partial<Record<string, string>> = {
  'Team Chat': 'nav-team-chat',
  Training: 'nav-training',
  Resources: 'nav-resources',
};

type MenuLink = {
  icon: LucideIcon;
  kind: 'link';
  name: string;
  path: (typeof routes)[keyof typeof routes];
};

const menu: MenuLink[] = [
  { icon: CalendarIcon, kind: 'link', name: 'Today', path: routes.todayPath },
  {
    icon: LineChartIcon,
    kind: 'link',
    name: 'My Goal',
    path: routes.progressPath,
  },
  {
    icon: MessageCircleIcon,
    kind: 'link',
    name: 'Team Chat',
    path: routes.chatPath,
  },
  {
    icon: GraduationCapIcon,
    kind: 'link',
    name: 'Training',
    path: routes.trainingPath,
  },
  {
    icon: FoldersIcon,
    kind: 'link',
    name: 'Resources',
    path: routes.resourcesPath,
  },
];

const [today, myGoal, teamChat, training, resources] = menu;
const askMe = { kind: 'ask-me' } as const;

// With the Assistant on, Ask Me takes the centre slot and Team Chat replaces My Goal.
const itemsFor = (
  chatEnabled: boolean,
  assistant: boolean,
): (MenuLink | typeof askMe)[] =>
  assistant
    ? [today, chatEnabled ? teamChat : myGoal, askMe, training, resources]
    : menu.filter((item) => item !== teamChat || chatEnabled);

const ITEM_CLASS =
  'flex relative flex-1 flex-col items-center justify-between gap-1 text-gray-400 aria-[current=page]:text-primary aria-[current=page]:pointer-events-none';

const BottomMenuContent = ({
  assistant,
  avatarUrl,
  chatEnabled,
}: {
  assistant: boolean;
  avatarUrl: null | string;
  chatEnabled: boolean;
}) => {
  const isKeyboardOpen = useKeyboardOpen();
  const { pathname } = useLocation();
  const { trigger } = useWebHaptics();
  const handleClick = () => {
    // Haptic feedback is best-effort; Safari can reject with non-Error values.
    void (trigger() ?? Promise.resolve()).catch(() => {});
  };

  // The Assistant page is full-screen and has its own way back.
  if (isAssistantPage(pathname)) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex border-t border-gray-200 bg-white fixed w-full bottom-0 safe-area-bottom pt-2 z-10',
        isKeyboardOpen && 'hidden',
      )}
    >
      {itemsFor(chatEnabled, assistant).map((item) => {
        if (item.kind === 'ask-me') {
          return (
            <NavLink
              className={ITEM_CLASS}
              key={assistantCopy.ASK_ME}
              onClick={handleClick}
              to={routes.assistantPath}
            >
              {({ isActive }) => (
                <>
                  <CopilotAvatar
                    className="size-7"
                    dimGlow={isActive}
                    name={assistantCopy.ASSISTANT_NAME}
                    src={avatarUrl}
                  />
                  <div className="text-sm font-medium">
                    {assistantCopy.ASK_ME}
                  </div>
                </>
              )}
            </NavLink>
          );
        }
        const { icon: Icon, name, path } = item;
        return (
          <NavLink
            className={ITEM_CLASS}
            data-tour={TOUR_TARGET_BY_NAME[name]}
            key={name}
            to={path}
            onClick={handleClick}
          >
            <Icon size={28} />
            <div className="text-sm flex items-center gap-1 font-medium">
              {name} {name === 'Team Chat' && <Chat />}
              {name === 'Training' && (
                <DeferredMount phase="background">
                  <SuspenseBoundary
                    fallback={undefined}
                    loadingFallback={undefined}
                  >
                    <Training />
                  </SuspenseBoundary>
                </DeferredMount>
              )}
            </div>
          </NavLink>
        );
      })}
    </div>
  );
};

const BottomMenuWithTeam = () => {
  const { data: team } = useGetTeam();
  return (
    <BottomMenuContent
      assistant={canUseAssistant(team)}
      avatarUrl={team?.copilotAvatarUrl ?? null}
      chatEnabled={Boolean(team?.chatEnabled)}
    />
  );
};

const BottomMenu = () => (
  <Suspense
    fallback={
      <BottomMenuContent
        assistant={false}
        avatarUrl={null}
        chatEnabled={false}
      />
    }
  >
    <BottomMenuWithTeam />
  </Suspense>
);

export default BottomMenu;
